import React, { useState } from 'react';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { Pagination } from '../../../components/Pagination/Pagination';
import { SortableList } from '../../../components/SortableList/SortableList';
import { usePaginatedResponse, useData, useRemoteData } from '../../../model/data';
import { getVenues, Venue } from '../../../model/venue';
import { getCountries } from '../../../model/country';
import { useQueryAsState } from '../../../model/history';
import { venuePath } from '../../../routes';
import { useMeta } from '../../../model/meta';
import { VenueForm } from './VenuesModal/VenueForm';
import { Icon } from '../../../components/Icon/Icon';
import { FilterBar } from '../../../components/FilterBar/FilterBar';
import { isAdmin, useSelectedMembership } from '../../../model/membership';
import { t } from '../../../locales/translate';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import './Venues.css';
import { compact } from 'lodash';
import { SortByIcon } from '../../../components/SortableList/SortByIcon';

export function Venues(): JSX.Element {
  const [params] = useQueryAsState();
  const [showAddVenueModal, setShowAddVenueModal] = useState(false);

  const [countries] = useRemoteData(getCountries, (db) => db.table('countries').all);
  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getVenues({
        page,
        perPage,
        query: params.query,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      }),
    JSON.stringify(params)
  );

  useMeta({ title: t.model.venue._plural });

  const venues = useData((db) => {
    const resp = compact(paginator.currentIds.map((e) => db.table('venues').find(e)));
    return resp.map((item: Venue) => ({
      ...item,
      layouts: item.venueLayouts ? item.venueLayouts.length : 0,
    }));
  });

  const membership = useSelectedMembership();

  return (
    <Container>
      <Card
        title={t.model.venue._plural}
        elevation={2}
        pagination={<Pagination {...paginator} />}
        actions={
          isAdmin(membership) && (
            <Icon
              name="plus"
              onClick={() => {
                setShowAddVenueModal(true);
              }}
            />
          )
        }
        fullbleed
      >
        <div>
          <SortableList
            items={venues}
            rowLink={(row) => venuePath(row)}
            error={paginator.error}
            actions={<FilterBar filters={['query']} />}
            loading={paginator.loading}
            columns={[
              {
                label: t.model.venue.venueName,
                apiSortIcon: <SortByIcon sortBy="name" />,
                dataKey: 'name',
                width: '250px',
                cellRenderer: (row) => row.name,
              },
              {
                label: t.model.address._name,
                dataKey: 'address',
                width: '180px',
                cellRenderer: (row) => [row.address.address, row.address.city].filter(Boolean).join(' - '),
              },
              {
                label: t.model.venueLayout.seatmaps,
                dataKey: 'layouts',
                width: '180px',
                cellRenderer: (row) => row.layouts,
              },
              {
                label: t.model.address.capacity,
                dataKey: 'venueLayouts',
                width: '180px',
                cellRenderer: (row) => {
                  return (
                    row.venueLayouts && row.venueLayouts.map((venueLayout) => venueLayout.capacity || 0).join(', ')
                  );
                },
              },
            ]}
          />
        </div>
      </Card>
      <ControlledModal<Venue>
        open={showAddVenueModal}
        model={{} as Venue}
        onClose={() => setShowAddVenueModal(false)}
        title={t.actions.add(t.model.venue._name)}
        size="small"
        overflow={false}
      >
        {(controller) => {
          return <VenueForm controller={controller} countries={countries} />;
        }}
      </ControlledModal>
    </Container>
  );
}
